import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "w-full max-w-2xl mx-auto grid gap-2" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = {
  key: 0,
  class: "flex flex-col items-center p-6 text-center"
}
const _hoisted_4 = { class: "p-4 rounded-full bg-white/10 mb-4" }
const _hoisted_5 = {
  key: 1,
  class: "relative w-full h-full flex items-center justify-center p-4"
}
const _hoisted_6 = ["src"]
const _hoisted_7 = ["src"]
const _hoisted_8 = {
  key: 2,
  class: "flex flex-col items-center w-full max-w-full"
}
const _hoisted_9 = { class: "p-4 rounded-full bg-white/10 mb-4" }
const _hoisted_10 = { class: "text-sm mb-2 text-center text-white/80 truncate max-w-full" }
const _hoisted_11 = ["src"]
const _hoisted_12 = ["title"]
const _hoisted_13 = {
  key: 2,
  class: "absolute inset-x-0 bottom-0 bg-black/70 p-2"
}
const _hoisted_14 = { class: "block text-center text-sm text-white mt-1" }

import { ref, watch } from 'vue'
import { LinkIcon, XIcon, MusicIcon, Eye, EyeOff } from 'lucide-vue-next'
import { Label } from '../label';
import { useToast } from '../toast';

interface Props {
  label?: string
  modelValue?: File | null // Para v-model
  preview?: string // URL da prévia existente (opcional)
  hasSpoiler?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AnyFileUpload',
  props: {
    label: { default: 'Mídia inicial' },
    modelValue: { default: null },
    preview: { default: '' },
    hasSpoiler: { type: Boolean }
  },
  emits: ['update:modelValue', 'update:preview', 'update:hasSpoiler'],
  setup(__props: any, { emit: __emit }) {

const { toast } = useToast()

const props = __props

const emit = __emit // Emitindo o evento para atualizar o v-model

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const selectedFile = ref<File | null>(props.modelValue || null) // Inicializando com o valor do v-model
const previewUrl = ref('')
const error = ref('')
const uploading = ref(false)
const progress = ref(0)
const isImage = ref(false)
const isVideo = ref(false)
const isAudio = ref(false)

// Verificar o tipo de mídia com base na prévia ou arquivo
const determineMediaType = (file?: File, previewUrl?: string) => {
  if (file) {
    isImage.value = file.type.startsWith('image/')
    isVideo.value = file.type.startsWith('video/')
    isAudio.value = file.type.startsWith('audio/')
  } else if (previewUrl) {
    const fileExtension = previewUrl.split('.').pop()?.toLowerCase() || ''
    isImage.value = previewUrl.startsWith('data:image') || ['jpeg', 'jpg', 'gif', 'png', 'avif', 'webp'].includes(fileExtension)
    isVideo.value = previewUrl.startsWith('data:video') || ['mp4', 'webm', 'ogg'].includes(fileExtension)
    isAudio.value = previewUrl.startsWith('data:audio') || ['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(fileExtension)
  }
}

// Determina o tipo de mídia inicial
if (props.preview) {
  determineMediaType(undefined, props.preview)
}

// Atualizar o previewUrl quando o modelValue mudar externamente
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    selectedFile.value = newValue
    previewUrl.value = URL.createObjectURL(newValue)
    determineMediaType(newValue)
  } else {
    selectedFile.value = null
    previewUrl.value = ''
    isImage.value = false
    isVideo.value = false
    isAudio.value = false
  }
})

const triggerFileInput = () => {
  if (!selectedFile.value && !props.preview) {
    fileInput.value?.click()
  }
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  processFile(file)
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  const file = event.dataTransfer?.files[0]
  processFile(file)
}

const processFile = (file?: File) => {
  if (!file || props.preview) return // Ignora se estiver no modo de prévia
  
  if (file.type.startsWith('image/')) {
    isImage.value = true
    isVideo.value = false
    isAudio.value = false
  } else if (file.type.startsWith('video/')) {
    isImage.value = false
    isVideo.value = true
    isAudio.value = false
  } else if (file.type.startsWith('audio/')) {
    isImage.value = false
    isVideo.value = false
    isAudio.value = true
  } else {
    toast({
      variant: 'destructive',
      description: 'Por favor, selecione apenas arquivos de imagem, vídeo ou áudio.'
    })
    error.value = 'Por favor, selecione apenas arquivos de imagem, vídeo ou áudio.'
    return
  }

  // Verificar tamanho do arquivo com base no tipo
  const maxSize = isAudio.value ? 10 * 1024 * 1024 : // 10MB para áudio
                 isVideo.value ? 50 * 1024 * 1024 : // 50MB para vídeo
                 5 * 1024 * 1024; // 5MB para imagem
  
  const fileTypeText = isAudio.value ? 'áudio' : isVideo.value ? 'vídeo' : 'imagem';
  const maxSizeMB = maxSize / (1024 * 1024);

  if (file.size > maxSize) {
    toast({
      variant: 'destructive',
      description: `O arquivo de ${fileTypeText} deve ter menos que ${maxSizeMB}MB.`
    })
    error.value = `O arquivo de ${fileTypeText} deve ter menos que ${maxSizeMB}MB.`
    return
  }

  error.value = ''
  selectedFile.value = file
  previewUrl.value = URL.createObjectURL(file)
  emit('update:modelValue', file) // Emitindo o novo valor para o v-model
  simulateUpload()
}

const removeFile = () => {
  selectedFile.value = null
  previewUrl.value = ''
  error.value = ''
  progress.value = 0
  uploading.value = false
  isImage.value = false
  isVideo.value = false
  isAudio.value = false
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  emit('update:preview', null) // Emitindo null para o v-model ao remover o arquivo
  emit('update:modelValue', null) // Emitindo null para o v-model ao remover o arquivo
}

const simulateUpload = () => {
  if (props.preview) return // Ignora se estiver no modo de prévia

  uploading.value = true
  progress.value = 0
  
  const interval = setInterval(() => {
    progress.value += 10
    if (progress.value >= 100) {
      clearInterval(interval)
      uploading.value = false
    }
  }, 70)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Label), null, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_ctx.label), 1)
      ]),
      _: 1
    }),
    _createElementVNode("div", {
      class: _normalizeClass(["relative min-h-[200px] max-h-[200px] rounded-lg border-2 border-dashed transition-all duration-300 flex items-center justify-center cursor-pointer", [ 
        isDragging.value && !_ctx.preview
          ? 'border-emerald-400 bg-emerald-400/5' 
          : 'border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10',
        (selectedFile.value || _ctx.preview) ? 'border-solid' : 'border-dashed'
      ]]),
      onDragenter: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (isDragging.value = true), ["prevent"])),
      onDragleave: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (isDragging.value = false), ["prevent"])),
      onDragover: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"])),
      onDrop: _withModifiers(handleDrop, ["prevent"]),
      onClick: triggerFileInput
    }, [
      _createElementVNode("input", {
        type: "file",
        ref_key: "fileInput",
        ref: fileInput,
        class: "hidden",
        onChange: handleFileSelect,
        accept: "image/*,video/mp4,audio/*",
        disabled: !!_ctx.preview
      }, null, 40, _hoisted_2),
      (!selectedFile.value && !_ctx.preview)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(LinkIcon), { class: "w-8 h-8 text-primary" })
            ]),
            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-white" }, [
              _createTextVNode(" Selecione uma imagem, vídeo ou áudio "),
              _createElementVNode("span", { class: "block text-sm text-white/60" }, " ou arraste e solte aqui ")
            ], -1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (isImage.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: previewUrl.value || _ctx.preview,
                  alt: "Preview",
                  class: _normalizeClass(["max-w-full max-h-[200px] rounded object-contain transition-all duration-300", { 'blur-md grayscale brightness-50': _ctx.hasSpoiler }])
                }, null, 10, _hoisted_6))
              : (isVideo.value)
                ? (_openBlock(), _createElementBlock("video", {
                    key: 1,
                    src: previewUrl.value || _ctx.preview,
                    controls: "",
                    class: _normalizeClass(["max-w-full max-h-[200px] rounded object-contain transition-all duration-300", { 'blur-md grayscale brightness-50': _ctx.hasSpoiler }])
                  }, null, 10, _hoisted_7))
                : (isAudio.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, [
                        _createVNode(_unref(MusicIcon), { class: "w-8 h-8 text-primary" })
                      ]),
                      _createElementVNode("p", _hoisted_10, _toDisplayString(selectedFile.value?.name || 'Arquivo de áudio'), 1),
                      _createElementVNode("audio", {
                        src: previewUrl.value || _ctx.preview,
                        controls: "",
                        class: "w-full max-w-full"
                      }, null, 8, _hoisted_11)
                    ]))
                  : _createCommentVNode("", true),
            ((selectedFile.value || _ctx.preview) && !isAudio.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 3,
                  onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (_ctx.$emit('update:hasSpoiler', !_ctx.hasSpoiler)), ["stop"])),
                  class: _normalizeClass(["absolute top-3 right-12 p-1.5 rounded-full transition-all duration-300 border backdrop-blur-md", [
            _ctx.hasSpoiler 
              ? 'bg-amber-500/20 border-amber-500 text-amber-500' 
              : 'bg-white/10 border-white/20 text-white/60 hover:text-white hover:bg-white/20'
          ]]),
                  title: _ctx.hasSpoiler ? 'Remover spoiler' : 'Adicionar spoiler'
                }, [
                  (_ctx.hasSpoiler)
                    ? (_openBlock(), _createBlock(_unref(EyeOff), {
                        key: 0,
                        class: "w-4 h-4"
                      }))
                    : (_openBlock(), _createBlock(_unref(Eye), {
                        key: 1,
                        class: "w-4 h-4"
                      }))
                ], 10, _hoisted_12))
              : _createCommentVNode("", true),
            (selectedFile.value || _ctx.preview)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 4,
                  onClick: _withModifiers(removeFile, ["stop"]),
                  class: "absolute top-3 right-3 p-1.5 rounded-full transition-colors border border-red-500 bg-red-500/10 hover:bg-red-500/20 group",
                  "aria-label": "Remover mídia"
                }, [
                  _createVNode(_unref(XIcon), { class: "w-4 h-4 text-red-500 group-hover:scale-110 transition-transform" })
                ]))
              : _createCommentVNode("", true)
          ])),
      (uploading.value && !_ctx.preview)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createElementVNode("div", {
              class: "h-1 bg-emerald-400 transition-all duration-300",
              style: _normalizeStyle({ width: `${progress.value}%` })
            }, null, 4),
            _createElementVNode("span", _hoisted_14, _toDisplayString(progress.value) + "% ", 1)
          ]))
        : _createCommentVNode("", true)
    ], 34)
  ]))
}
}

})